// =============================================================================
// io/sync/saveExitModel.ts — create, update and delete an exit model.
// -----------------------------------------------------------------------------
// The simplest writer in this client, and worth saying why it is allowed to be:
//
// ── 🚨 WHY THIS NEEDS NO HYDRATION LEDGER, WHERE A TRADE DOES (D46/D71) ──
//
// The ledger exists because `tradeToDb` writes all 48 columns unconditionally, so
// a trade saved from a partial read destroys the columns it never loaded. An exit
// model has FOUR fields and the editor renders every one of them. There is no
// column the form does not own, so there is nothing a partial read could lose —
// the safety property comes free from the shape of the table, not from a guard.
//
// ⚠ That reasoning is the thing to check if this table ever grows a column. A
// fifth field that the editor does not render turns this into exactly the write
// D46 forbids, and nothing here would notice.
//
// ── UPDATE BY ID, INSERT FOR NEW ──
//
// Same split as `saveTrade`, same reason: an upsert on a mistyped id silently
// creates a phantom row, whereas an update matching nothing is detectable and an
// insert colliding is a loud duplicate-key error.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';

export interface ExitModelPatch {
  name: string;
  stopRule: string;
  targetRule: string;
  scaleOutRule: string;
}

/**
 * ⚠ Every field the editor shows, and no more. Derived from the patch type so a
 * field cannot be added to the form and silently not written.
 */
function toRow(p: ExitModelPatch, userId: string): Record<string, unknown> {
  return {
    user_id: userId,
    name: p.name,
    // ⚠ Empty string, not null. The old client writes `e.stopRule || null`, but
    // reading it back turns null into '' anyway — and round-tripping '' → null →
    // '' means every save of an untouched empty field rewrites the column. Kept
    // as '' so a no-op save is genuinely a no-op.
    stop_rule: p.stopRule,
    target_rule: p.targetRule,
    scale_out_rule: p.scaleOutRule,
  };
}

export interface SaveExitModelResult {
  id: string | null;
  error: string | null;
}

/**
 * Validate before writing.
 *
 * ⚠ A NAME IS REQUIRED, and that is not decoration: the trade form refuses to
 * save a trade that has a pattern and no exit model, so an unnamed model becomes
 * an unpickable option in a required field. The rules may all be empty — Josh's
 * DISCRETIONARY model is exactly that, and "no rules" is a real exit approach.
 */
export function validateExitModel(p: ExitModelPatch): string | null {
  if (!p.name.trim()) return 'Give the exit model a name — it has to be pickable on the trade form.';
  return null;
}

export async function saveExitModel(
  sb: SupabaseClient, id: string, patch: ExitModelPatch, userId: string,
): Promise<SaveExitModelResult> {
  const invalid = validateExitModel(patch);
  if (invalid) return { id: null, error: invalid };

  const { data, error } = await sb
    .from('exit_models').update(toRow(patch, userId)).eq('id', id).select('id');

  if (error) {
    return {
      id: null,
      error: reportFailureAs('save exit model', error, `Save failed: ${error.message}`),
    };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return {
      id: null,
      error: 'The save matched no rows, so nothing changed. The exit model may have been '
        + 'deleted, or it belongs to another user.',
    };
  }
  return { id, error: null };
}

export async function insertExitModel(
  sb: SupabaseClient, id: string, patch: ExitModelPatch, userId: string,
): Promise<SaveExitModelResult> {
  const invalid = validateExitModel(patch);
  if (invalid) return { id: null, error: invalid };
  if (!id) return { id: null, error: 'Refusing to insert an exit model with no id.' };

  const { error } = await sb.from('exit_models').insert({ id, ...toRow(patch, userId) });
  if (error) {
    return {
      id: null,
      error: reportFailureAs('add exit model', error, `Save failed: ${error.message}`),
    };
  }
  return { id, error: null };
}

/**
 * Delete an exit model.
 *
 * ⚠ `trades.exit_model_id` is a foreign key with `ON DELETE SET NULL`, so this
 * does NOT fail when trades reference the model — it silently untags them. The
 * caller must say so in the confirmation, because the count of affected trades is
 * invisible from here and un-taggging a year of trades is not recoverable by
 * re-creating a model with the same name.
 */
export async function deleteExitModel(
  sb: SupabaseClient, userId: string, id: string,
): Promise<{ error: string | null }> {
  const { data, error } = await sb
    .from('exit_models').delete().eq('user_id', userId).eq('id', id).select('id');
  if (error) {
    return { error: reportFailureAs('delete exit model', error, `Delete failed: ${error.message}`) };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return { error: 'The delete matched no rows — the exit model may already be gone.' };
  }
  return { error: null };
}
